import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RejectWithMessage, TableDataResponse } from 'features/types';
import { getErrorMessage } from 'utils/error';
import { RootState } from 'app/root-reducer';
import { Paging, Tab } from 'api/types';
import { JobSeekerResourcesState, JobSeekerResourcesTableView, JobSeekerResourcesRequestData, JobSeekerResourcesTabs, JobSeekerResourcesTableFilters, GetJobSeekerResourcesTabsFilters, JobSeekerResourcesTableRequestFiltersData, JobSeekerResourcesFiltersData, ResourcesViewMode } from './types';
import { resourcesAPI } from 'api';

export const getJobSeekerResourcesTable = createAsyncThunk<TableDataResponse<JobSeekerResourcesTableView>, ResourcesViewMode, RejectWithMessage>(
  'job-seeker-resources/get-table',
  async (resourcesViewMode, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState();
      const requestData = state.jobSeekerResources.requestData;
      const requestWithViewMode = { ...requestData, filters: { ...requestData.filters, resourcesViewMode, } };
      return resourcesAPI.getJobSeekerTable(requestWithViewMode)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getJobSeekerResourcesTabs = createAsyncThunk<Tab[], ResourcesViewMode, RejectWithMessage>(
  'job-seeker-resources/get-job-seekers-tabs',
  async (resourcesViewMode, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState();
      const requestData = state.jobSeekerResources.requestData;
      const requestWithViewMode = { ...requestData, filters: { ...requestData.filters, resourcesViewMode, } };
      return resourcesAPI.getJobSeekerResourcesTabsData(requestWithViewMode);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getJobSeekerResourcesFiltersData = createAsyncThunk<JobSeekerResourcesFiltersData, ResourcesViewMode, RejectWithMessage>(
  'job-seeker-resources/get-job-seeker-resources-filters-data',
  async (resourcesViewMode, { rejectWithValue }) => {
    try {
      return await resourcesAPI.getJobSeekerResourcesFiltersData(resourcesViewMode);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const initialRequestData: JobSeekerResourcesRequestData = {
  paging: {
    page: 1,
    pageSize: 20,
  },
  filters: {
    tab: JobSeekerResourcesTabs.All,
  },
};

const getInitialFiltersByViewMode = (mode: ResourcesViewMode): GetJobSeekerResourcesTabsFilters => {
  const filters: GetJobSeekerResourcesTabsFilters = { tab: JobSeekerResourcesTabs.All, location: null };
  if (mode === ResourcesViewMode.Events) filters.postType = { value: 'Event', text: 'Event' };
  return filters;
};

const initialState: JobSeekerResourcesState = {
  status: 'idle',
  resourcesViewMode: undefined,
  requestData: initialRequestData,
  currentFilters: { tab: JobSeekerResourcesTabs.All, location: null },
  data: {
    tableDataIsLoading: false,
    tabsIsLoading: false,
    tabs: [],
    tableData: {
      rows: [],
      totalCount: 0,
      noResult: false,
    },
    filters: null
  },
  error: null
};

const jobSeekerResourcesSlice = createSlice({
  name: 'job-seeker-resources',
  initialState,
  reducers: {
    setResourcesViewMode: (state, action: PayloadAction<ResourcesViewMode>) => {
      const mode = action.payload;
      if (mode && mode === state.resourcesViewMode) return;
      state.resourcesViewMode = mode;
      const filters = getInitialFiltersByViewMode(mode);
      state.currentFilters = filters;
      state.requestData = { paging: initialRequestData.paging, filters: { tab: filters.tab, postType: filters.postType?.value } };
    },

    setPage: (state, action: PayloadAction<Paging>) => {
      state.requestData.paging.page = action.payload.page;
      state.requestData.paging.pageSize = action.payload.pageSize;
    },
    setFilters: (state, action: PayloadAction<Partial<JobSeekerResourcesTableFilters>>) => {
      const payloadFilters = action.payload;
      const newRequestFilters: Partial<JobSeekerResourcesTableRequestFiltersData> = {
        ...payloadFilters,
        assignedPrograms: payloadFilters.assignedPrograms?.map((item) => item.value),
        postType: payloadFilters.postType?.value || '',
        eventFormat: payloadFilters.eventFormat?.value || '',
        jobSeekerEventRsvp: payloadFilters.jobSeekerEventRsvp?.value || '',
        fromDate: payloadFilters.fromDate ? new Date(payloadFilters.fromDate).toISOString() : undefined,
        toDate: payloadFilters.toDate ? new Date(payloadFilters.toDate).toISOString() : undefined,
      };
      state.requestData.filters = { ...state.requestData.filters, ...newRequestFilters };
      state.requestData.paging = initialRequestData.paging;

      const newFilters = { ...state.currentFilters, ...action.payload };
      state.currentFilters = newFilters;
    },
    setTab: (state, action: PayloadAction<string>) => {
      state.currentFilters.tab = action.payload;
      state.requestData.filters.tab = action.payload;
    },
    setLike: (state, action) => {
      const { resourceId, postLikes, postLiked } = action.payload;
      state.data.tableData.rows = state.data.tableData.rows.map(row => {
        if (row.id === resourceId) {
          return { ...row, postLikes, postLiked };
        }
        return row;
      });
    },
    setRsvp: (state, action) => {
      const { resourceId, eventRsvps, jobSeekerEventRsvp } = action.payload;
      state.data.tableData.rows = state.data.tableData.rows.map(row => {
        if (row.id === resourceId) {
          return { ...row, eventRsvps, jobSeekerEventRsvp };
        }
        return row;
      });
    },
    resetRequestData: (state) => {
      state.requestData = { ...initialState.requestData };
    },
    resetTableData: (state) => {
      state.data.tableData = initialState.data.tableData;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getJobSeekerResourcesTable.pending, (state) => {
      state.status = 'pending';
      state.data.tableDataIsLoading = true;
    });
    builder.addCase(getJobSeekerResourcesTable.fulfilled, (state, action: PayloadAction<TableDataResponse<JobSeekerResourcesTableView>>) => {
      state.status = 'fulfilled';
      state.data.tableDataIsLoading = false;
      state.data.tableData = action.payload;
      state.data.tableData.noResult = state.data.tableData.rows.length === 0;
      state.error = null;
    });
    builder.addCase(getJobSeekerResourcesTable.rejected, (state) => {
      state.status = 'rejected';
      state.data.tableDataIsLoading = false;
    });

    builder.addCase(getJobSeekerResourcesFiltersData.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(getJobSeekerResourcesFiltersData.fulfilled, (state, action: PayloadAction<JobSeekerResourcesFiltersData>) => {
      state.status = 'fulfilled';
      state.data.filters = action.payload;
      state.error = null;
    });
    builder.addCase(getJobSeekerResourcesFiltersData.rejected, (state) => {
      state.status = 'rejected';
    });

    builder.addCase(getJobSeekerResourcesTabs.pending, (state) => {
      state.data.tabsIsLoading = true;
    });
    builder.addCase(getJobSeekerResourcesTabs.fulfilled, (state, action: PayloadAction<Tab[]>) => {
      state.data.tabsIsLoading = false;
      state.data.tabs = action.payload;
    });
    builder.addCase(getJobSeekerResourcesTabs.rejected, (state) => {
      state.data.tabsIsLoading = false;
    });
  }
});

export const { setResourcesViewMode, setPage, setTab, setFilters, setLike, setRsvp, resetRequestData, resetTableData, resetState } = jobSeekerResourcesSlice.actions;

export const jobSeekerResourcesReducer = jobSeekerResourcesSlice.reducer;
